import { Button } from '@ipfabric/ui-components/Button/Button';
import { TableButton } from '@ipfabric/ui-components/TableButton/TableButton';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import { prop } from '@ipfabric/utils/fp/prop';
import { reloadTableData } from 'components/ApiTable/model/commands/reloadTableData';
import { ApiTablePageView } from 'components/ApiTablePage/ApiTablePageView';
import { useApiTablePage } from 'components/ApiTablePage/useApiTablePage';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import type {
  TableActionsItem,
  TableBulkAction,
  TableBulkActionClickEvent,
} from 'components/Table/model/TableModel';
import { useLocale } from 'containers/App/LocaleContext';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { OuiForm } from 'pages/Settings/DiscoverySnapshots/GlobalDiscoveryConfiguration/Oui/OuiForm';
import { type FC, memo, useMemo, useRef, useState } from 'react';
import { useNotifications } from 'utils/hooks/useNotifications';
import { useApi } from 'utils/request/useApi';
import { useTableEndpoint } from 'utils/table/useTableEndpoint';
import { useResponsiveTableButton } from '../../../../../features/DiscoverySettingsForms/utils/useResponsiveTableButton';

const ouiApiEndpoint = '/tables/reports/settings-oui';

type RowData = {
  oui: string;
  vendor: string;
  enabled: boolean;
};

const BareOuiPage: FC = () => {
  const ouiMsg = useLocale().settings.oui;
  const [editedOui, setEditedOui] = useState<RowData | 'new' | null>(null);
  const [resettingSettings, setResettingSettings] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const confirmDialog = useSimpleConfirmDialog();
  const api = useApi();
  const notifications = useNotifications();
  const bulkEnableSelectedRows = useStableCallback(
    async (
      { selectedRows, indicateRunningAction }: TableBulkActionClickEvent,
      enabled: boolean,
    ) => {
      await indicateRunningAction(() =>
        api.request({
          url: '/settings/oui',
          method: 'PATCH',
          data: Array.from(selectedRows).map((id) => ({
            id,
            enabled,
          })),
        }),
      );
      ouiApiTablePage.apiTableModel.dispatch(reloadTableData);
    },
  );

  const bulkActions = useMemo(
    (): TableBulkAction[] => [
      {
        key: 'enable',
        title: 'Enable',
        icon: 'fa-check',
        onClick: (bulkActionClickEvent: TableBulkActionClickEvent) =>
          bulkEnableSelectedRows(bulkActionClickEvent, true),
      },
      {
        key: 'disable',
        title: 'Disable',
        icon: 'fa-xmark',
        accent: 'negative',
        onClick: (bulkActionClickEvent: TableBulkActionClickEvent) =>
          bulkEnableSelectedRows(bulkActionClickEvent, false),
      },
    ],
    [],
  );

  const endpoint = useTableEndpoint({
    apiEndpoint: ouiApiEndpoint,
    alternative: (originalEndpoint) => ({
      id: 'oui',
      columns: [
        '...',
        createFrontendOnlyColumn({
          key: 'action',
          disabledSort: true,
          title: ouiMsg.actionsColumn,
          webDependencies: ['oui', 'vendor', 'enabled'],
          render: (rowData: RowData) => (
            <TableButton
              dataTestId={`oui-${rowData.oui}-edit-icon`}
              icon='fa-pen'
              onClick={() => setEditedOui(rowData)}
              variant='plain'
            />
          ),
        }),
      ],
      // load all columns because we use loaded data to fetch form values
      webDependencies: originalEndpoint.columns.map(prop('key')),
    }),
  });

  const handleClearSettingsClick = useStableCallback(async () => {
    if (!(await confirmDialog.show(ouiMsg.resetSettingsConfirmDialog))) {
      return;
    }
    try {
      setResettingSettings(true);
      await api.request({
        url: '/settings/oui/reset',
        method: 'POST',
      });
      ouiApiTablePage.apiTableModel.dispatch(reloadTableData);
      notifications.showNotification({
        type: 'success',
        message: ouiMsg.ouiResetSuccess,
      });
    } finally {
      setResettingSettings(false);
    }
  });

  const { actionItem: resetActionItem } = useResponsiveTableButton({
    label: ouiMsg.btnClearSettings,
    icon: 'fa-arrows-rotate',
    onClick: handleClearSettingsClick,
    containerRef: tableContainerRef,
    size: 'small',
    variant: 'outline',
    loading: resettingSettings,
  });

  const menuItems = useMemo(
    () =>
      [
        {
          standalone: true,
          elementWithKey: (
            <Button
              key='add'
              variant='outline'
              size='small'
              icon='fa-plus-circle'
              onClick={() => {
                setEditedOui('new');
              }}
            >
              {ouiMsg.btnAdd}
            </Button>
          ),
        } satisfies TableActionsItem,
        resetActionItem,
      ].filter(Boolean),
    [ouiMsg.btnAdd, resetActionItem],
  );

  const ouiApiTablePage = useApiTablePage({
    endpoint,
    additionalApiTableProps: {
      bulkActions,
      rowIdField: 'oui',
      menuItems,
    },
  });

  return (
    <div className='flex flex-col flex-1 gap' ref={tableContainerRef}>
      <div className='text-level-4 font-heading font-semibold'>OUI</div>
      <ApiTablePageView model={ouiApiTablePage} />
      {confirmDialog.render()}
      {editedOui !== null && (
        <OuiForm
          editedOui={editedOui}
          onClose={() => setEditedOui(null)}
          onSaveSuccess={() => {
            ouiApiTablePage.apiTableModel.dispatch(reloadTableData);
            setEditedOui(null);
          }}
        />
      )}
    </div>
  );
};

export const OuiPage = memo(BareOuiPage);
